import type { TableCellStyle } from '../../models/table/tableCell.types';
import type { TableCellKey } from '../../../../stores/tableCellStore';

type Rect = { x: number; y: number; w: number; h: number };
type Insets = { top: number; right: number; bottom: number; left: number };

const toNonNegativeNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }
  return undefined;
};

const resolveInsets = (
  style: TableCellStyle | undefined,
  keys: {
    all: 'padding' | 'margin';
    top: 'paddingTop' | 'marginTop';
    right: 'paddingRight' | 'marginRight';
    bottom: 'paddingBottom' | 'marginBottom';
    left: 'paddingLeft' | 'marginLeft';
  },
): Insets => {
  const base = toNonNegativeNumber(style?.[keys.all]) ?? 0;

  return {
    top: toNonNegativeNumber(style?.[keys.top]) ?? base,
    right: toNonNegativeNumber(style?.[keys.right]) ?? base,
    bottom: toNonNegativeNumber(style?.[keys.bottom]) ?? base,
    left: toNonNegativeNumber(style?.[keys.left]) ?? base,
  };
};

export const getTableCellStyleFromAttrs = (
  attrs: Record<string, unknown>,
  cellKey: TableCellKey,
): TableCellStyle | undefined => {
  const cellStyles = (attrs.cellStyles ?? {}) as Record<string, unknown>;
  const style = cellStyles[cellKey];
  return style && typeof style === 'object' ? (style as TableCellStyle) : undefined;
};

export const resolveTableCellPadding = (style: TableCellStyle | undefined): Insets => {
  return resolveInsets(style, {
    all: 'padding',
    top: 'paddingTop',
    right: 'paddingRight',
    bottom: 'paddingBottom',
    left: 'paddingLeft',
  });
};

export const resolveTableCellMargin = (style: TableCellStyle | undefined): Insets => {
  return resolveInsets(style, {
    all: 'margin',
    top: 'marginTop',
    right: 'marginRight',
    bottom: 'marginBottom',
    left: 'marginLeft',
  });
};

export const insetTableRect = (rect: Rect, insets: Insets): Rect => {
  const horizontal = insets.left + insets.right;
  const vertical = insets.top + insets.bottom;

  return {
    x: rect.x + insets.left,
    y: rect.y + insets.top,
    w: Math.max(1, rect.w - horizontal),
    h: Math.max(1, rect.h - vertical),
  };
};

export const getTableCellTextRect = (rect: Rect, style: TableCellStyle | undefined): Rect => {
  return insetTableRect(rect, resolveTableCellPadding(style));
};

export const getTableCellContentRect = (rect: Rect, style: TableCellStyle | undefined): Rect => {
  return insetTableRect(rect, resolveTableCellMargin(style));
};
